import { useState } from 'react';

import { adminCases, adminServices } from '../api/admin';
import CaseCard from '../components/CaseCard';
import type { CaseStudy } from '../types/content';
import { AreaField, ListField, SelectField, TextField } from './fields';
import PreviewOverlay from './PreviewOverlay';
import { useCollection } from './useCollection';
import { useDirty, useUnsavedChanges } from './useUnsavedChanges';

const EMPTY: CaseStudy = {
  id: '',
  level: 2,
  title: '',
  tags: [],
  situation: '',
  challenge: '',
  role: '',
  approach: '',
  outcome: '',
  insight: '',
  serviceId: null,
  serviceTitle: null,
  titleEn: '',
  situationEn: '',
  challengeEn: '',
  roleEn: '',
  approachEn: '',
  outcomeEn: '',
  insightEn: ''
};

const LEVELS = [
  { value: '1', label: '1 – Namngivet case' },
  { value: '2', label: '2 – Anonymiserat case' },
  { value: '3', label: '3 – Selected experience' }
];

export default function CasesPanel() {
  const { items, error, reload } = useCollection(adminCases.list);
  const services = useCollection(adminServices.list);
  const { confirmLeave } = useUnsavedChanges();
  const [draft, setDraft] = useState<CaseStudy | null>(null);
  const [original, setOriginal] = useState<CaseStudy | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);

  useDirty(draft !== null && JSON.stringify(draft) !== JSON.stringify(original));

  const serviceOptions = [
    { value: '', label: 'Ingen koppling' },
    ...(services.items ?? []).map((service) => ({ value: service.id, label: service.title }))
  ];

  function open(item: CaseStudy, id: string | null) {
    if (!confirmLeave()) return;
    setDraft({ ...item });
    setOriginal({ ...item });
    setEditingId(id);
    setSaveError(null);
  }

  function close() {
    setDraft(null);
    setOriginal(null);
    setEditingId(null);
  }

  function change(patch: Partial<CaseStudy>) {
    setDraft((current) => (current ? { ...current, ...patch } : current));
  }

  async function save() {
    if (!draft) return;
    setSaving(true);
    setSaveError(null);
    try {
      if (editingId) {
        await adminCases.update(editingId, draft);
      } else {
        await adminCases.create(draft);
      }
      close();
      reload();
    } catch {
      setSaveError('Kunde inte spara. Kontrollera att id:t är unikt och att alla fält är ifyllda.');
    } finally {
      setSaving(false);
    }
  }

  async function remove(item: CaseStudy) {
    if (!window.confirm(`Ta bort caset "${item.title}"?`)) return;
    await adminCases.remove(item.id);
    if (editingId === item.id) {
      close();
    }
    reload();
  }

  return (
    <>
      <div className={draft ? 'admin-layout is-editing' : 'admin-layout'}>
        <div className="admin-column">
          <div className="admin-head">
            <h2>Case</h2>
            <button
              className="button primary small"
              type="button"
              onClick={() => open(EMPTY, null)}
            >
              Nytt case
            </button>
          </div>

          {error && <div className="state is-error">{error}</div>}

          <div className="admin-list">
            {items?.map((item) => (
              <div className="admin-row" key={item.id}>
                <div>
                  <strong>{item.title}</strong>
                  <div className="meta">
                    Nivå {item.level} · {item.tags.join(', ')} · {item.id}
                  </div>
                </div>
                <div className="admin-actions">
                  <button
                    className="button secondary small"
                    type="button"
                    onClick={() => open(item, item.id)}
                  >
                    Redigera
                  </button>
                  <button className="button danger small" type="button" onClick={() => remove(item)}>
                    Ta bort
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {draft && (
          <div className="admin-panel">
            <div className="panel-head">
              <h3>{editingId ? `Redigerar ${editingId}` : 'Nytt case'}</h3>
              <button className="button secondary small" type="button" onClick={() => setPreview(true)}>
                Förhandsgranska
              </button>
            </div>
          <TextField
            label="Id"
            hint="(används i URL:en)"
            value={draft.id}
            onChange={(value) => change({ id: value })}
          />
          <div className="field-row">
            <TextField label="Titel" value={draft.title} onChange={(value) => change({ title: value })} />
            <SelectField
              label="Publiceringsnivå"
              value={String(draft.level)}
              options={LEVELS}
              onChange={(value) => change({ level: Number(value) })}
            />
          </div>
          <ListField
            label="Taggar"
            hint="(en per rad – används som filter)"
            value={draft.tags}
            onChange={(value) => change({ tags: value })}
          />
          <AreaField
            label="Situation"
            value={draft.situation}
            onChange={(value) => change({ situation: value })}
          />
          <AreaField
            label="Utmaning"
            value={draft.challenge}
            onChange={(value) => change({ challenge: value })}
          />
          <AreaField label="Roll" value={draft.role} onChange={(value) => change({ role: value })} />
          <AreaField
            label="Angreppssätt"
            value={draft.approach}
            onChange={(value) => change({ approach: value })}
          />
          <AreaField label="Resultat" value={draft.outcome} onChange={(value) => change({ outcome: value })} />
          <AreaField
            label="Viktigaste lärdomen"
            value={draft.insight}
            onChange={(value) => change({ insight: value })}
          />
          <SelectField
            label="Relaterad tjänst"
            value={draft.serviceId ?? ''}
            options={serviceOptions}
            onChange={(value) => change({ serviceId: value || null })}
          />

          <div className="lang-section">
            <h4>
              Engelska <span className="hint">(valfritt – tomma fält visar svenskan)</span>
            </h4>
            <TextField
              label="Titel"
              value={draft.titleEn ?? ''}
              onChange={(value) => change({ titleEn: value })}
            />
            <AreaField
              label="Situation"
              value={draft.situationEn ?? ''}
              onChange={(value) => change({ situationEn: value })}
            />
            <AreaField
              label="Utmaning"
              value={draft.challengeEn ?? ''}
              onChange={(value) => change({ challengeEn: value })}
            />
            <AreaField
              label="Roll"
              value={draft.roleEn ?? ''}
              onChange={(value) => change({ roleEn: value })}
            />
            <AreaField
              label="Angreppssätt"
              value={draft.approachEn ?? ''}
              onChange={(value) => change({ approachEn: value })}
            />
            <AreaField
              label="Resultat"
              value={draft.outcomeEn ?? ''}
              onChange={(value) => change({ outcomeEn: value })}
            />
            <AreaField
              label="Viktigaste lärdomen"
              value={draft.insightEn ?? ''}
              onChange={(value) => change({ insightEn: value })}
            />
          </div>

          {saveError && <div className="state is-error">{saveError}</div>}

          <div className="admin-actions">
            <button className="button primary" type="button" onClick={save} disabled={saving}>
              {saving ? 'Sparar…' : 'Spara'}
            </button>
            <button className="button secondary" type="button" onClick={() => confirmLeave() && close()}>
              Avbryt
            </button>
          </div>
        </div>
        )}
      </div>

      {draft && preview && (
        <PreviewOverlay title="Så visas caset på /case" onClose={() => setPreview(false)}>
          <div className="case-grid">
            <CaseCard
              item={{
                ...draft,
                serviceTitle:
                  (services.items ?? []).find((service) => service.id === draft.serviceId)?.title ?? null
              }}
            />
          </div>
        </PreviewOverlay>
      )}
    </>
  );
}
